import "./date-countdown.js";

const SPEED = 750;

class SectionPromoBar extends HTMLElement {
  setHeightVariable = () =>
    document.documentElement.style.setProperty(
      "--promo-bar-height",
      `${this.getBoundingClientRect().height}px`,
    );

  connectedCallback() {
    window.requestAnimationFrame(this.setHeightVariable);
    window.addEventListener("resize", this.setHeightVariable);

    this.slides = this.querySelectorAll(".swiper-slide");

    // Fired when a countdown expires and removes itself, changing slide height.
    this.addEventListener("promo-bar:update", () => this.swiper?.update());

    if (this.slides.length < 2) return;

    this._initCarousel();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.setHeightVariable);
    this.swiper?.destroy();
  }

  _activeSlide() {
    if (!this.swiper) return null;

    return this.swiper.slides[this.swiper.activeIndex];
  }

  _initCarousel() {
    // Keeps the colour change in step with the slide motion.
    this.style.setProperty("--promo-bar-speed", `${SPEED}ms`);

    window.loadSwiper.then(
      ({ Swiper, Autoplay, Navigation, EffectCreative }) => {
        this.swiper = new Swiper(this.querySelector(".swiper"), {
          modules: [Autoplay, Navigation, EffectCreative],
          slidesPerView: 1,
          speed: SPEED,
          loop: true,
          allowTouchMove: false,
          effect: "creative",
          creativeEffect: {
            prev: { translate: [0, "100%", 0], opacity: 0 },
            next: { translate: [0, "-100%", 0], opacity: 0 },
          },
          a11y: { enabled: false },
          autoplay: {
            // Each slide carries its own delay via `data-swiper-autoplay`.
            delay: 6000,
            disableOnInteraction: true,
          },
          navigation: {
            nextEl: this.querySelector("[data-promo-bar-navigation-next]"),
            prevEl: this.querySelector("[data-promo-bar-navigation-previous]"),
          },
          on: {
            slideChangeTransitionStart: () => {
              const activeSlide = this._activeSlide();
              const { colorBg, color } = activeSlide.dataset;

              this.style.setProperty("--color-bg", colorBg);
              this.style.setProperty("--color", color);

              this.swiper.slides.forEach((slide) => {
                slide.setAttribute("aria-hidden", "true");
                slide.querySelectorAll("a, button").forEach((el) => {
                  el.tabIndex = -1;
                });
              });
            },
            slideChangeTransitionEnd: () => {
              const activeSlide = this._activeSlide();

              activeSlide.removeAttribute("aria-hidden");
              activeSlide.querySelectorAll("a, button").forEach((el) => {
                el.tabIndex = null;
              });
            },
            lock: () => this.classList.add("promo-bar--locked"),
            unlock: () => this.classList.remove("promo-bar--locked"),
            autoplayStop: () => this._setAutoplayState(),
          },
        });

        const activeSlide = this._activeSlide();

        this.swiper.slides.forEach((slide) => {
          if (slide === activeSlide) return;

          slide.setAttribute("aria-hidden", "true");
          slide.querySelectorAll("a, button").forEach((el) => {
            el.tabIndex = -1;
          });
        });

        this.querySelector(
          "[data-promo-bar-autoplay-toggle]",
        )?.addEventListener("click", () => this._toggleAutoplay());

        this._setAutoplayState();
      },
    );
  }

  // Deliberately stop/start rather than pause/resume. Swiper sets
  // `autoplay.paused` itself for the length of every transition, and resumes
  // any pause when the tab becomes visible again — so a user pause would
  // misread mid-transition and be undone by switching tabs. A stopped
  // autoplay is left alone by both.
  _toggleAutoplay() {
    const { autoplay } = this.swiper;

    if (autoplay.running) {
      autoplay.stop();
    } else {
      autoplay.start();
    }

    this._setAutoplayState();
  }

  _setAutoplayState() {
    if (!this.swiper) return;

    this.setAttribute(
      "state",
      this.swiper.autoplay.running ? "playing" : "paused",
    );
  }
}

if (!customElements.get("section-promo-bar")) {
  customElements.define("section-promo-bar", SectionPromoBar);
}
